import { Controller, ControllerProps, FieldValues } from "react-hook-form";
import { View, Text } from "react-native";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
} from "ui/gluestack/form-control";
import { AlertCircleIcon } from "ui/gluestack/icon";
import { requiredValidation } from "ui/utils/Validators";

export interface Props<TFieldValues extends FieldValues = FieldValues> {
  id: string;
  name: ControllerProps<TFieldValues>["name"];
  label?: string;
  placeholder?: string;
  helperText?: string;
  invalidMessage?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  control: ControllerProps<TFieldValues>["control"];
  rules?: ControllerProps<TFieldValues>["rules"];
  className?: string;
}

export function FormControlledDatePicker<
  TFieldValues extends FieldValues = FieldValues,
>({
  id,
  name,
  isInvalid,
  isReadOnly,
  isDisabled,
  isRequired,
  placeholder,
  label,
  invalidMessage,
  helperText,
  control,
  rules,
  className,
}: Props<TFieldValues>) {
  const localRules = {
    required: isRequired ? requiredValidation(label ?? "Field") : undefined,
    ...rules,
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error }, field: { value, onChange, name } }) => {
        // <input type="date"> only accepts YYYY-MM-DD. Strip any time/offset
        // suffix so ISO datetime values from the API render correctly on edit.
        const dateInputValue =
          typeof value === "string" && value.length >= 10
            ? value.slice(0, 10)
            : "";

        return (
        <FormControl
          isInvalid={isInvalid || !!error}
          size="md"
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          isRequired={isRequired}
        >
          {label ? (
            <Text className="text-sm font-medium mb-2 text-gray-600">
              {label}
              {!isRequired ? " (Optional)" : ""}
            </Text>
          ) : null}
          <View className={`relative mb-1 ${className}`}>
            <input
              type="date"
              id={id}
              name={name}
              value={dateInputValue}
              disabled={isDisabled}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              className={`w-full border rounded text-gray-900 h-10 px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isInvalid || !!error
                  ? "border-2 border-error-700"
                  : "border-gray-300"
              }`}
              placeholder={placeholder}
            />
          </View>
          {helperText ? (
            <FormControlHelper>
              <FormControlHelperText className="text-gray-500">
                {helperText ?? " "}
              </FormControlHelperText>
            </FormControlHelper>
          ) : null}
          {invalidMessage || error?.message ? (
            <FormControlError>
              <FormControlErrorIcon
                as={AlertCircleIcon}
                className="text-error-500"
                size="sm"
              />
              <FormControlErrorText className="text-error-500 text-sm">
                {error?.message || invalidMessage}
              </FormControlErrorText>
            </FormControlError>
          ) : null}
        </FormControl>
        );
      }}
      rules={localRules}
    />
  );
}
